import { SiteUrls } from '@tableau/shared-urls';
import * as _ from 'underscore';
import { SpacesSiteUrls } from '../../app/spaces/SpacesSiteUrls';
import { WindowLocationService } from '../../commonInfra/services/WindowLocationService';
import { Projects } from '../../projects/resources/Projects';
import { Views } from '../../views/resources/Views';
import { ISiteSettingsForServerAdmin } from '../../settings/types/ISite';
import { ITagWithCount } from '../directives/tbTagsInput';
import { ILocLinkifierModel } from '../filters/LocLinkifierFilter';
import { IActionContext } from '../helpers/ActionContext';
import { Filter } from '../helpers/Filter';
import { SpaceInfoItemTypeValue } from '../models/ContentType';
import { IContentInfo } from '../models/IContentInfo';
import { IDataRoleBase } from '../models/IDataRoleInfo';
import { IDataSourceBase, IDataSourceDetailInfo } from '../models/IDataSourceInfo';
import { IProjectBase, IProjectDetailInfo, IProjectInfo } from '../models/IProjectInfo';
import { ISliceable } from '../models/ISliceable';
import { IViewBase, IViewDetailInfo } from '../models/IViewInfo';
import { IWorkbookBase, IWorkbookDetailInfo, IWorkbookInfo } from '../models/IWorkbookInfo';
import { IErrorItem } from '../models/MultipleItemResult';
import {
  IDownloadItemInfo,
  IEditableItemInfo,
  INamedItemInfo,
  IOwnedItemInfo,
  ISpaceTypeInfo,
  ITabbedItemInfo,
  ITaggedItemInfo,
} from '../models/resourceInfoParts';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { DataSources } from '../resources/Datasources';
import { Workbooks } from '../resources/Workbooks';
import { IResult } from '../ServerApi/types/IResponse';
import { IUserName } from '../ServerApi/types/IUser';
import { ActionHelpers } from './ActionHelpers';
import { ChangeContentOwnerAction } from './ChangeContentOwnerAction';
import { ChangeContentTagsAction } from './ChangeContentTagsAction';
import { ChangeDatasourceRemoteQueryAgentAction } from './ChangeDatasourceRemoteQueryAgentAction';
import { ChangeDatasourcesTagsAction } from './ChangeDatasourcesTagsAction';
import { ChangeViewsTagsAction } from './ChangeViewTagsAction';
import { ChangeWorkbooksTagsAction } from './ChangeWorkbooksTagsAction';
import { CreateProjectAction } from './CreateProjectAction';
import { DeleteContentsAction } from './DeleteContentsAction';
import { DeleteSitesAction } from './DeleteSitesAction';
import { EditSiteAction } from './EditSiteAction';
import { MoveToProjectAction } from './MoveToProjectAction';
import { RenameAction } from './RenameAction';
import { SetDisplayTabsAction } from './SetDisplayTabsAction';

export interface IConfirmDialogTypeaheadAttributes<T> {
  model: T;
  displayField: string;
  suggestions: (matchText: string) => ISliceable<any>;
  matchPrefixesOnly?: boolean;
}

export interface IConfirmDialogTypeaheadScope<T> extends ng.IScope {
  description: string;
  typeahead: IConfirmDialogTypeaheadAttributes<T>;
}

export interface IConfirmChangeOwnerScope extends ng.IScope {
  ownerPicker: {
    prompt: string;
    ownerQuery: (query: string) => ISliceable<IUserName>;
    targetOwner: IUserName;
  };
}

export interface IConfirmChangeWorkbookOwnerScope extends ng.IScope {
  ownerPicker: {
    prompt: string;
    promptDescriptionLinkifierModel: () => ILocLinkifierModel;
    ownerQuery: (query: string) => ISliceable<IUserName>;
    targetOwner: IUserName;
  };
}

export interface IConfirmDialogTagsScope extends ng.IScope {
  description: string;
  tags: {
    model: ITagWithCount[];
  };
}

export interface IDeleteAction {
  (set: Set<any>, actionContext: IActionContext): ng.IPromise<any>;
}

export interface IDownloadAction {
  (downloadItem: IDownloadItemInfo): any;
}

export interface IChangeOwnerAction {
  (set: Set<INamedItemInfo & IOwnedItemInfo>, actionContext: IActionContext): ng.IPromise<any>;
}

export interface IUpdateSiteAvailabilityAction {
  (set: Set<ISiteSettingsForServerAdmin>, actionContext: IActionContext): ng.IPromise<any>;
}

export interface IEditSiteAction {
  (site: ISiteSettingsForServerAdmin, actionContext: IActionContext): ng.IPromise<any>;
}

export interface IEditWorkbookAction {
  (item: IEditableItemInfo): any;
}

export interface IChangeTagsAction {
  (set: Set<ITaggedItemInfo>, actionContext: IActionContext): ng.IPromise<any>;
}

export interface ISetDisplayTabsAction {
  (set: Set<ITabbedItemInfo>, actionContext: IActionContext): ng.IPromise<any>;
}

export class ContentActions {

  public static $inject = [
    'Workbooks',
    'DataSources',
    'MoveToProjectAction',
    'CreateProjectAction',
    'ChangeContentTagsAction',
    'ChangeViewsTagsAction',
    'ChangeWorkbooksTagsAction',
    'ChangeDatasourcesTagsAction',
    'DeleteSitesAction',
    'EditSiteAction',
    'ChangeDatasourceRemoteQueryAgentAction',
    'SetDisplayTabsAction',
    'ChangeContentOwnerAction',
    'DeleteContentsAction',
  ];

  constructor(
    private Workbooks: Workbooks,
    private DataSources: DataSources,
    private MoveToProjectAction: MoveToProjectAction,
    private CreateProjectAction: CreateProjectAction,
    private ChangeContentTagsAction: ChangeContentTagsAction,
    private ChangeViewsTagsAction: ChangeViewsTagsAction,
    private ChangeWorkbooksTagsAction: ChangeWorkbooksTagsAction,
    private ChangeDatasourcesTagsAction: ChangeDatasourcesTagsAction,
    private DeleteSitesAction: DeleteSitesAction,
    private EditSiteAction: EditSiteAction,
    private ChangeDatasourceRemoteQueryAgentAction: ChangeDatasourceRemoteQueryAgentAction,
    private SetDisplayTabsAction: SetDisplayTabsAction,
    private ChangeContentOwnerAction: ChangeContentOwnerAction,
    private DeleteContentsAction: DeleteContentsAction) { }

  // Delete actions

  public deleteProject(projectDetail: IProjectDetailInfo, actionContext: IActionContext): ng.IPromise<IResult> {
    return Projects.all()
      .withFilters([Filter.fieldEq('id', projectDetail.id)])
      .getFirst()
      .then((projectInfo: IProjectInfo) => {
        return this.deleteProjects(this.singleton(projectInfo), actionContext);
      });
  }

  public deleteProjects(projects: Set<IProjectInfo>, actionContext: IActionContext): ng.IPromise<IResult> {
    return this.DeleteContentsAction.confirmDeleteContents(projects.toArray(), actionContext);
  }

  public deleteWorkbook(workbookDetail: IWorkbookDetailInfo, actionContext: IActionContext): ng.IPromise<IResult> {
    return this.Workbooks.all()
      .withFilters([Filter.fieldEq('id', workbookDetail.id)])
      .getFirst()
      .then((workbookInfo: IWorkbookInfo) => {
        return this.deleteWorkbooks(this.singleton(workbookInfo), actionContext);
      });
  }

  public deleteWorkbooks(workbooks: Set<IWorkbookInfo>, actionContext: IActionContext): ng.IPromise<IResult> {
    return this.DeleteContentsAction.confirmDeleteContents(workbooks.toArray(), actionContext);
  }

  // Move to Project

  public moveProjectsToProject(projects: Set<IProjectBase>, actionContext: IActionContext): ng.IPromise<IResult> {
    return this.MoveToProjectAction.moveProjects(projects, actionContext);
  }

  public moveWorkbooksToProject(workbooks: Set<IWorkbookBase>, actionContext: IActionContext): ng.IPromise<IResult> {
    return this.MoveToProjectAction.moveWorkbooks(workbooks, actionContext);
  }

  public moveDatasourcesToProject(datasources: Set<IDataSourceBase>, actionContext: IActionContext): ng.IPromise<IResult> {
    return this.MoveToProjectAction.moveDatasources(datasources, actionContext);
  }

  public moveDataRolesToProject(dataRoles: Set<IDataRoleBase>, actionContext: IActionContext): ng.IPromise<IResult> {
    return this.MoveToProjectAction.moveDataRoles(dataRoles, actionContext);
  }

  // Create project
  // Returns the new project id
  public createProject(actionContext: IActionContext, parentId?: string): ng.IPromise<IResult> {
    return this.CreateProjectAction.execute(actionContext, parentId);
  }

  // Helpers

  private singleton<T extends INamedItemInfo>(item: T) {
    return Set.singleton(item, (item: T) => item.id);
  }

  // Rename

  public renameProjectPopup(project: INamedItemInfo): ng.IPromise<IResult> {
    return RenameAction.renameProject(project);
  }

  public renameWorkbookPopup(workbook: INamedItemInfo): ng.IPromise<IResult> {
    return RenameAction.renameWorkbook(workbook);
  }

  public renameViewPopup(view: INamedItemInfo): ng.IPromise<IResult> {
    return RenameAction.renameView(view);
  }

  // Download

  public changeOwnerOnDatasources(datasources: Set<IDataSourceBase>, actionContext: IActionContext): ng.IPromise<IResult> {
    return this.ChangeContentOwnerAction.confirmChangeOwner(datasources.toArray(), actionContext);
  }

  public changeOwnerOnProjects(projects: Set<IProjectBase>, actionContext: IActionContext): ng.IPromise<IResult> {
    return this.ChangeContentOwnerAction.confirmChangeOwner(projects.toArray(), actionContext);
  }

  public changeOwnerOnWorkbooks(workbooks: Set<IWorkbookBase>, actionContext: IActionContext): ng.IPromise<IResult> {
    return this.ChangeContentOwnerAction.confirmChangeOwner(workbooks.toArray(), actionContext);
  }

  // Edit Site Settings

  public editSite(site: ISiteSettingsForServerAdmin, actionContext: IActionContext): ng.IPromise<void> {
    return this.EditSiteAction.execute(site, actionContext);
  }

  // Delete Sites

  public deleteSites(sites: Set<ISiteSettingsForServerAdmin>, actionContext: IActionContext): ng.IPromise<any> {
    return this.DeleteSitesAction.execute(sites, actionContext);
  }

  // Authoring

  private authoring(authoringUrl: string) {
    WindowLocationService.open(authoringUrl);
  }

  private generateUniqueAuxiliaryId() {
    const base36Digit = () => {
      const s = '0123456789abcdefghijklmnopqrstuvwxyz';
      return s[Math.floor(Math.random() * s.length)];
    };

    const b36 = (len: number) => {
      let s = '';
      for (let i = 0; i < len; ++i) {
        s += base36Digit();
      }
      return s;
    };

    // To generate the auxiliary ID, use a base-32 encoding of the client time-stamp plus a guid-like base-36 pseudo-unique identifier.
    // We use half the number of digits compared to a standard GUID since base-36 has over twice the number of encoding bits.
    // A collision of identifiers generated in this way is virtually impossible.
    return (new Date()).getTime().toString(32).toLowerCase() + '$' + b36(4) + '-'  + b36(2) + '-' + b36(2) + '-' + b36(2) + '-' + b36(6);
  }

  public editWorkbook(workbook: IWorkbookDetailInfo) {
    this.authoring(workbook.editUrl);
  }

  public editView(view: IViewDetailInfo) {
    this.authoring(view.editUrl);
  }

  public newFlow(siteUrlName: string, spaceItemInfo: ISpaceTypeInfo, projectId?: string) {
    this.authoring(this.generateNewFlowUrl(siteUrlName, spaceItemInfo, projectId));
  }

  public newPublishedConnection(projectLuid?: string) {
    this.authoring(this.publishedConnectionEditorUrlForNewPublishedConnection(projectLuid));
  }

  public editPublishedConnection(publishedConnectionId: string) {
    this.authoring(this.publishedConnectionEditorUrlForExistingPublishedConnection(publishedConnectionId));
  }

  private publishedConnectionEditorUrlForNewPublishedConnection(projectLuid?: string) {
    if (projectLuid) {
      return `${this.publishedConnectionEditorPrefix}?project=${projectLuid}`;
    } else {
      return this.publishedConnectionEditorPrefix;
    }
  }

  private publishedConnectionEditorUrlForExistingPublishedConnection(publishedConnectionId: string) {
    return `${this.publishedConnectionEditorPrefix}?published-connection=${publishedConnectionId}`;
  }

  private readonly publishedConnectionEditorPrefix = '/published-connection-editor/';

  public editFlow(siteUrlName: string, flowLuid: string) {
    this.authoring(this.generateEditFlowUrl(siteUrlName, flowLuid));
  }

  public editUnpublishedFlowDraft(urls: SpacesSiteUrls, draftLuid: string) {
    this.authoring(urls.flowEditorDraftUrl(draftLuid));
  }

  public generateNewFlowUrl(siteUrlName: string, spaceItemInfo: ISpaceTypeInfo, projectId?: string) {
    const urlSearchParams = new URLSearchParams();
    if (spaceItemInfo.contentType === SpaceInfoItemTypeValue.datasource) {
      urlSearchParams.set('datasource', (spaceItemInfo as IDataSourceDetailInfo).urlName);
    }
    if (projectId) {
      urlSearchParams.set('projectId', projectId);
    }
    const urlSearchTerm: string = urlSearchParams.toString();
    const siteUrl = new SiteUrls({ siteUrlName });

    return `${siteUrl.flowEditorFlowPath({ flowLuid: 'new' })}${urlSearchTerm ? `?${urlSearchTerm}` : ''}`;
  }

  public generateEditFlowUrl(siteUrlName: string, flowLuid: string) {
    const siteUrl = new SiteUrls({ siteUrlName });
    return siteUrl.flowEditorFlowPath({ flowLuid: flowLuid });
  }

  public newEmptyWorkbook(siteName: string, projectName?: string) {
    this.authoring(this.generateNewWorkbookUrl('newWorkbook', siteName, projectName));
  }

  public generateNewWorkbookFromDatasourceUrl(datasource: IDataSourceDetailInfo, siteName: string) {
    return `${this.generateNewWorkbookUrl('authoringNewWorkbook', siteName)}/${datasource.urlName}`;
  }

  public generateNewWorkbookUrl(newWorkbookUrlName: string, siteName: string, projectName?: string) {
    return (siteName === '' ? '' : '/t/' + siteName) +
      '/' + newWorkbookUrlName + '/' +
      (!projectName || projectName === '' ? '' : 'p/' + projectName + '/') +
      this.generateUniqueAuxiliaryId();
  }

  public newMetric(datasource: IDataSourceDetailInfo, siteName: string) {
    WindowLocationService.open(this.generateNewMetricForDatasourceUrl(datasource, siteName));
  }

  public generateNewMetricForDatasourceUrl(datasource: IDataSourceDetailInfo, siteName: string) {
    let site = siteName === '' ? '' : '/t/' + siteName;
    return `${site}/metrics/new/${this.generateUniqueAuxiliaryId()}?:ds=${datasource.urlName}&:dsId=${datasource.id}`;
  }

  // Change Tags

  public setTagsOnViews(views: Set<IViewBase>, actionContext: IActionContext): ng.IPromise<IResult> {
    return this.ChangeViewsTagsAction.execute(views, actionContext);
  }

  public setTagsOnView(view: IViewBase, newTagsList: string[]): ng.IPromise<IResult> {
    const tagsToAdd = _.difference(newTagsList, view.tags);
    const tagsToRemove = _.difference(view.tags, newTagsList);

    return Views.changeTags([view.id], tagsToAdd, tagsToRemove)
      .then(result => ActionHelpers.notifyStandardError(
        Set.fromIdItemArray([view]),
        result,
        ChangeViewsTagsAction.notificationTranslationFns,
        (errorItem: IErrorItem<IViewBase>) => this.ChangeViewsTagsAction.changeTagsErrorDetail(errorItem)
      ));
  }

  public setTagsOnWorkbook(workbook: IWorkbookBase, newTagsList: string[]): ng.IPromise<IResult> {
    const tagsToAdd = _.difference(newTagsList, workbook.tags);
    const tagsToRemove = _.difference(workbook.tags, newTagsList);

    return this.Workbooks.changeTags([workbook.id], tagsToAdd, tagsToRemove)
      .then(result => ActionHelpers.notifyStandardError(
        Set.fromIdItemArray([workbook]),
        result,
        ChangeWorkbooksTagsAction.notificationTranslationFns,
        (errorItem: IErrorItem<IWorkbookBase>) => this.ChangeWorkbooksTagsAction.changeTagsErrorDetail(errorItem)
      ));
  }

  public setTagsOnWorkbooks(workbooks: Set<IWorkbookBase>, actionContext: IActionContext): ng.IPromise<IResult> {
    return this.ChangeWorkbooksTagsAction.execute(workbooks, actionContext);
  }

  public setTagsOnDatasource(datasource: IDataSourceDetailInfo, newTagsList: string[]): ng.IPromise<IResult> {
    const tagsToAdd = _.difference(newTagsList, datasource.tags);
    const tagsToRemove = _.difference(datasource.tags, newTagsList);

    return this.DataSources.changeTags([datasource.id], tagsToAdd, tagsToRemove)
      .then(result => ActionHelpers.notifyStandardError(
        Set.fromIdItemArray([datasource]),
        result,
        ChangeDatasourcesTagsAction.notificationTranslationFns,
        (errorItem: IErrorItem<IDataSourceBase>) => this.ChangeDatasourcesTagsAction.changeTagsErrorDetail(errorItem)
      ));
  }

  public setTagsOnDatasources(datasources: Set<IDataSourceBase>, actionContext: IActionContext): ng.IPromise<IResult> {
    return this.ChangeDatasourcesTagsAction.showChangeTagsDialog(datasources, actionContext);
  }

  public setTagsOnMixedContent(content: IContentInfo[], actionContext: IActionContext): ng.IPromise<IResult> {
    return this.ChangeContentTagsAction.showChangeTagsForMixedContentDialog(content, actionContext);
  }

  // Set Display Tabs

  public setDisplayTabsOnWorkbook(workbooks: Set<IWorkbookBase>, actionContext: IActionContext): ng.IPromise<IResult> {
    return this.SetDisplayTabsAction.confirmSetDisplayTabs(workbooks, actionContext);
  }

  public changeRemoteQueryAgentOnDatasource(datasource: IDataSourceDetailInfo): ng.IPromise<IResult> {
    return this.ChangeDatasourceRemoteQueryAgentAction.execute(datasource);
  }

  public openWhoHasSeenThisView(viewId: string) {
    const whoHasSeenThisViewLink = '/vizql/showadminview/views/WhoHasSeen?views_id=' + viewId;
    WindowLocationService.open(whoHasSeenThisViewLink);
  }
}

Module.instance().service('ContentActions', ContentActions);
